import { PathLayer, IconLayer } from '@deck.gl/layers';
import { SUBWAY_GEOMETRY_VERSION } from '@vitals/shared';
import type { SubwayGeometry, SubwayLineFeature, SubwayStationFeature, SubwayTravelDirection } from '@vitals/shared';
import { buildSubwayStationLayer } from './stationMarkers.js';

/**
 * The static subway map: colored route lines + station dots, drawn beneath the
 * live train bullets. Geometry is fetched once from the server's cached
 * `/api/subway-geometry` endpoint (it never changes between GTFS refreshes), so
 * these layers are built once and reused — they don't animate.
 */

/** Fetch the static subway geometry once. Returns null on failure so the rest of
 * the map renders normally without it (honest degradation, not a hard crash). */
export async function fetchSubwayGeometry(baseUrl = ''): Promise<SubwayGeometry | null> {
  try {
    // The ?v= schema version busts the long HTTP cache on any payload-shape
    // change, so a returning client never pairs new JS with stale cached JSON.
    const res = await fetch(`${baseUrl}/api/subway-geometry?v=${SUBWAY_GEOMETRY_VERSION}`);
    if (!res.ok) return null;
    return (await res.json()) as SubwayGeometry;
  } catch {
    return null;
  }
}

const LINE_ALPHA = 200; // route lines sit just under full opacity so overlaps blend
const FALLBACK_LINE: [number, number, number] = [136, 136, 136]; // grey if color missing

function directionAxis(start: [number, number], end: [number, number]): SubwayTravelDirection[] {
  const dLng = end[0] - start[0];
  const dLat = end[1] - start[1];
  const eastWest = Math.abs(dLng * Math.cos(((start[1] + end[1]) / 2) * Math.PI / 180));
  return Math.abs(dLat) >= eastWest
    ? ['Northbound', 'Southbound'] : ['Eastbound', 'Westbound'];
}

/** Colored line ribbons, offset side-by-side where colors share a corridor. */
export function buildSubwayLinesLayer(geometry: SubwayGeometry): PathLayer<SubwayLineFeature> {
  return new PathLayer<SubwayLineFeature>({
    id: 'subwayLines',
    data: geometry.lines.features,
    getPath: (f) => f.geometry.coordinates,
    getColor: (f) => {
      const [r, g, b] = f.properties.color ?? FALLBACK_LINE;
      return [r, g, b, LINE_ALPHA];
    },
    getWidth: 2,
    widthUnits: 'pixels',
    widthMinPixels: 1,
    capRounded: true,
    jointRounded: true,
    // Lines are decoration; stations are the hover target, so don't pick lines.
    pickable: false,
  });
}

/** Small station dots; hover surfaces the station name (handled in tooltip.ts). */
export function buildSubwayStationsLayer(
  geometry: SubwayGeometry,
): IconLayer<SubwayStationFeature> {
  return buildSubwayStationLayer('subwayStations', geometry.stations.features);
}

function segmentDistanceSquared(
  point: [number, number], start: [number, number], end: [number, number],
): number {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lengthSquared = dx * dx + dy * dy;
  const fraction = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / lengthSquared));
  const nearX = start[0] + fraction * dx;
  const nearY = start[1] + fraction * dy;
  return (point[0] - nearX) ** 2 + (point[1] - nearY) ** 2;
}

/** Infer each marker's cardinal heading pair from the closest drawn route segment. */
export function subwayStationDirectionAxes(geometry: SubwayGeometry): Record<string, SubwayTravelDirection[]> {
  const axes: Record<string, SubwayTravelDirection[]> = {};
  const RELEVANT_DISTANCE_SQUARED = 0.000_000_04; // ~20m; accommodates offset ribbons at an interchange.
  for (const station of geometry.stations.features) {
    const relevant: Array<{ distance: number; start: [number, number]; end: [number, number] }> = [];
    for (const line of geometry.lines.features) {
      const coordinates = line.geometry.coordinates;
      for (let i = 1; i < coordinates.length; i += 1) {
        const start = coordinates[i - 1]!;
        const end = coordinates[i]!;
        const distance = segmentDistanceSquared(station.geometry.coordinates, start, end);
        if (distance <= RELEVANT_DISTANCE_SQUARED) relevant.push({ distance, start, end });
      }
    }
    if (relevant.length === 0) continue;
    const selectionId = station.properties.selectionId;
    const existing = axes[selectionId] ?? [];
    axes[selectionId] = [...new Set([...existing, ...relevant.flatMap(({ start, end }) => {
      return directionAxis(start, end);
    })])];
  }
  return axes;
}
